import React from "react";
import PageSeo from "../seo/PageSeo";
import PageLayout from "../components/PageLayout";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../Theme";
import Slider from "react-slick";

type PoodleImage = {
    src: string;
    alt: string;
};

type PoodleProfile = {
    callName: string;
    name: string;
    description: string;
    images: PoodleImage[];
    ofaLink: string;
};

const poodlesData: PoodleProfile[] = [
    {
        callName: "Calcifer",
        name: "AmCH UCH URO1 Kvali’s the Fire Inside at Galia CGC BCAT SPOT",
        description:
            "Calcifer is our foundation stud and a truly exceptional Standard Poodle. He is handler-focused with a calm, steady temperament that makes him an ideal working service dog. Calcifer has completed all his health testing, exceeding CHIC standards. He’s not only intelligent and biddable, but also an incredibly sweet and affectionate companion who brings joy to everyone he meets.",
        images: [
            {
                src: "/CalciferChampion.jpg",
                alt: "Calcifer, black AKC champion standard poodle in show stack pose",
            },
            {
                src: "/CalciferExpression.jpg",
                alt: "Head study of Calcifer, black standard poodle with alert expression",
            },
            {
                src: "/CalciferAndEllie.jpg",
                alt: "Calcifer, black standard poodle, standing with handler Ellie in an outdoor field",
            },
            {
                src: "/CalciferCloseUp.jpg",
                alt: "Close-up portrait of Calcifer, black standard poodle, happy expression with tongue out",
            },
            {
                src: "/CalciferIndoor.jpg",
                alt: "Calcifer, black standard poodle, lying on a patterned rug indoors",
            },
            {
                src: "/CalciferSittingField.jpg",
                alt: "Calcifer, black standard poodle, sitting on grass in a sunny field",
            },
            {
                src: "/CalciferTrottingField.jpg",
                alt: "Calcifer, black standard poodle, trotting across an open grassy field",
            },
        ],
        ofaLink: "https://ofa.org/advanced-search/?appnum=2517341",
    },
    {
        callName: "Kaija",
        name: "Donnchada Kaija's Mellow Timbre CGC BCAT SPOT",
        description:
            "Kaija is a sweet and athletic girl who brings excellent structure and a balanced temperament to our program. She’s incredibly sporty with medium to high drive and a strong love for water. She naturally alerts to wildlife, showing potential for producing sport or hunting dogs. Kaija is currently working toward her UKC and AKC conformation titles. She is co-owned with Donnchada Poodles and has passed her health testing. She will be bred once a stud is approved by her co-owner, Betty Brown.",
        images: [
            {
                src: "/KaijaReserveFemaleUKC.jpg",
                alt: "Kaija, standard poodle, reserve placement at UKC conformation show",
            },
            {
                src: "/KaijaExpression.jpg",
                alt: "Head study of Kaija, standard poodle with soft expression",
            },
            {
                src: "/KaijaMovement.jpg",
                alt: "Kaija, standard poodle, moving at a trot in the show ring",
            },
            {
                src: "/KaijaIndoor.jpg",
                alt: "Kaija, standard poodle, lying on a patterned rug indoors",
            },
            {
                src: "/KaijaCloseUp.jpg",
                alt: "Close-up portrait of Kaija, standard poodle, looking up with happy expression",
            },
            {
                src: "/KaijaSittingField.jpg",
                alt: "Kaija, standard poodle, sitting on a lawn outdoors",
            },
        ],
        ofaLink: "https://ofa.org/advanced-search/?appnum=2503474",
    },
];

function SampleNextArrow(props: { onClick?: () => void }) {
    const { onClick } = props;
    return (
        <Box
            onClick={onClick}
            sx={{
                position: "absolute",
                top: "50%",
                right: -12,
                transform: "translateY(-50%)",
                cursor: "pointer",
                zIndex: 10,
                color: "rgba(0,0,0,0.6)",
                "&:hover": { color: "rgba(0,0,0,0.9)" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                borderRadius: 1,
            }}
        >
            <ArrowForwardIosIcon fontSize="small" />
        </Box>
    );
}

function SamplePrevArrow(props: { onClick?: () => void }) {
    const { onClick } = props;
    return (
        <Box
            onClick={onClick}
            sx={{
                position: "absolute",
                top: "50%",
                left: -12,
                transform: "translateY(-50%)",
                cursor: "pointer",
                zIndex: 10,
                color: "rgba(0,0,0,0.6)",
                "&:hover": { color: "rgba(0,0,0,0.9)" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                borderRadius: 1,
            }}
        >
            <ArrowBackIosNewIcon fontSize="small" />
        </Box>
    );
}

const calciferHero = poodlesData[0].images[0];

const OurPoodles: React.FC = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        adaptiveHeight: true,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
    };

    return (
        <>
            <PageSeo
                title="Our Poodles | Galia Standard Poodles"
                description="Meet Calcifer and Kaija, the AKC standard poodles behind our Austin, TX breeding program. View health testing and conformation details."
                path="/our-poodles"
                image={calciferHero.src}
                imageAlt={calciferHero.alt}
            />

            <PageLayout>
                    <Typography component="h1" variant="h4" gutterBottom>
                        Our Poodles
                    </Typography>

                    <Typography variant="body1" paragraph>
                        Meet the poodles behind our breeding program.
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 4} justifyContent="center">
                        {poodlesData.map((poodle, index) => (
                            <Grid item xs={12} key={index}>
                                <Card
                                    sx={{
                                        maxWidth: isDesktop ? "80%" : "100%",
                                        margin: "0 auto",
                                        boxShadow: "none",
                                        borderRadius: 3,
                                        padding: isMobile ? 1.5 : 3,
                                        marginTop: isMobile ? 2 : 6,
                                        backgroundColor: "white",
                                        display: "flex",
                                        flexDirection: isMobile ? "column" : "row",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? "100%" : "65%",
                                            px: isMobile ? 0 : 4,
                                            position: "relative",
                                            minHeight: isMobile ? 250 : "auto",
                                            overflowX: "hidden",
                                            "& .slick-dotted.slick-slider": {
                                                marginBottom: 0,
                                            },
                                            "& .slick-dots": {
                                                position: "relative",
                                                bottom: "auto",
                                                marginTop: 1.5,
                                                lineHeight: 0,
                                            },
                                        }}
                                    >
                                        <Slider {...sliderSettings}>
                                            {poodle.images.map((image) => (
                                                <Box key={image.src}>
                                                    <CardMedia
                                                        component="img"
                                                        image={image.src}
                                                        alt={image.alt}
                                                        sx={{
                                                            width: "100%",
                                                            height: "auto",
                                                            objectFit: "contain",
                                                            borderRadius: 2,
                                                            maxHeight: isMobile ? 280 : 400,
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Slider>
                                    </Box>

                                    <CardContent
                                        sx={{
                                            width: "100%",
                                            textAlign: "center",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "flex-start",
                                            px: isMobile ? 1 : 0,
                                            py: isMobile ? 0.5 : 2,
                                            mt: isMobile ? 1 : 0,
                                        }}
                                    >
                                        <Typography
                                            component="h2"
                                            variant="h4"
                                            sx={{
                                                fontWeight: "bold",
                                                mb: 0.25,
                                                mt: 0,
                                            }}
                                        >
                                            {poodle.callName}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontSize: isMobile ? "1.1rem" : "1.25rem",
                                                fontWeight: "bold",
                                                mb: 1,
                                            }}
                                        >
                                            {poodle.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 2 }}
                                        >
                                            {poodle.description}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                alignSelf: "center",
                                                textTransform: "none",
                                                px: 4,
                                                py: 1.5,
                                                fontWeight: "bold",
                                                borderRadius: 3,
                                            }}
                                            href={poodle.ofaLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Health Testing
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
            </PageLayout>
        </>
    );
};

export default OurPoodles;
